import { Avatar } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  AvatarWrapper,
  CallUnderDescriptionBannerLayoutRedesigned,
  CallUnderDescriptionBannerRedesigned,
} from 'shared/components/CallUnderDescriptionBannerRedesigned';
import { IconCoversation } from 'shared/components/CallUnderDescriptionBannerRedesigned/IconCoversation';
import { CardSectionNew } from 'shared/components/CardSectionNew';
import { HintQuestion, HintQuestions } from 'shared/components/HintQuestions';
import { useChats } from 'shared/hooks/useChats';
import { useIsElectronicTradingOffer } from 'shared/hooks/useIsElectronicTradingOffer';
import { useRenderContactButtons } from 'shared/hooks/useRenderContactButtons';
import { IChatHintSchema } from 'shared/repositories/chats/entities/chat_hint/ChatHintSchema';
import { selectIsOfferPublished, selectOffer, selectUser } from 'shared/selectors';
import { selectEarlyAccessContactsFlowEnabled } from 'shared/selectors/earlyAccess/selectEarlyAccessContactsFlowEnabled';
import { offerChatSelector } from 'shared/selectors/offerChat';
import { selectAgentAvatarUrl } from 'shared/selectors/offerData/agent';
import { selectAgent } from 'shared/selectors/selectAgent';
import { trackBlockedEarlyAccessChatClick } from 'shared/utils/tracking/earlyAccessTracking';

import { ChatStartMessageFormContainer } from './ChatStartMessageFormContainer';
import { PhonesContainer } from './parts';
import { trackHintQuestionClick } from './tracking';
import { useEarlyAccessPaywallContext } from '../../../packages/EarlyAccessPaywall';
import {
  EAccessCheckTrackingStatus,
  ESubscriptionLimitType,
} from '../../../packages/EarlyAccessPaywall/shared/types/context';
import { trackSendMessageClick } from '../ChatPopupContainer/tracking';

export const HintQuestionsContainerRedesigned: React.FC = () => {
  const { isChatsEnabled, hintQuestions } = useSelector(offerChatSelector);
  const offer = useSelector(selectOffer);
  const user = useSelector(selectUser);
  const isOfferPublished = useSelector(selectIsOfferPublished);
  const isElectronicTradingOffer = useIsElectronicTradingOffer();
  const { showPhoneButton } = useRenderContactButtons();
  const { handleOpenChat } = useChats();
  const agent = useSelector(selectAgent);

  const agentAvatarUrl = useSelector(selectAgentAvatarUrl);

  const { checkContactsAccess } = useEarlyAccessPaywallContext();
  const isEarlyAccessContactsFlowEnabled = useSelector(selectEarlyAccessContactsFlowEnabled);

  const handleEarlyAccessFlow = async (messageText: string) => {
    if (!user?.isAuthenticated) {
      trackBlockedEarlyAccessChatClick({
        offer,
        agent,
        blockType: EAccessCheckTrackingStatus.Unauthorized,
      });
    }

    const { isConfirmed, trackingStatus } = await checkContactsAccess({
      offerId: offer.id,
      limitType: ESubscriptionLimitType.Chat,
    });

    if (isConfirmed) {
      trackSendMessageClick({ offer, agent, isEarlyAccess: true });

      handleOpenChat({ defaultMessage: messageText, isEarlyAccess: true });
    } else {
      trackBlockedEarlyAccessChatClick({
        offer,
        agent,
        blockType: trackingStatus,
      });
    }
  };

  const onClick = React.useCallback(
    async (text: string) => {
      const { analyticsKey, messageText } = hintQuestions.find(
        ({ buttonText }) => buttonText === text,
      ) as IChatHintSchema;

      trackHintQuestionClick(offer, analyticsKey);

      if (isEarlyAccessContactsFlowEnabled) {
        await handleEarlyAccessFlow(messageText);

        return;
      }

      handleOpenChat({ defaultMessage: messageText });
    },
    [hintQuestions, offer, handleOpenChat],
  );

  if (
    isElectronicTradingOffer ||
    !isOfferPublished ||
    !isChatsEnabled ||
    user?.cianUserId === offer.cianUserId ||
    !hintQuestions.length
  ) {
    return null;
  }

  const avatarNode = agentAvatarUrl ? (
    <AvatarWrapper>
      <Avatar src={agentAvatarUrl} type="agent" size={48} />
    </AvatarWrapper>
  ) : (
    <IconCoversation />
  );

  return (
    <CardSectionNew data-name="HintQuestionsContainer">
      <CallUnderDescriptionBannerLayoutRedesigned>
        <CallUnderDescriptionBannerRedesigned
          title="Спросите в чате у автора объявления"
          fullWidth
          agentAvatar={avatarNode}
        >
          <ChatStartMessageFormContainer />
          <HintQuestions isRedesignedBlock>
            {hintQuestions.map(({ buttonText }, index) => (
              <HintQuestion key={index} onClick={onClick} text={buttonText} isRedesignedBlock />
            ))}
          </HintQuestions>
        </CallUnderDescriptionBannerRedesigned>
        {showPhoneButton && (
          <CallUnderDescriptionBannerRedesigned
            title="Или позвоните"
            addedStyles={agentAvatarUrl ? 'with-avatar' : 'without-avatar'}
          >
            <PhonesContainer size="M" isRedesignedBlock />
          </CallUnderDescriptionBannerRedesigned>
        )}
      </CallUnderDescriptionBannerLayoutRedesigned>
    </CardSectionNew>
  );
};
